import type { IMediaFile, IWebsiteMediaImportRsponse } from "@/shared/types";
import { api } from "./api";

export const mediaLibApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMediaLibFiles: builder.mutation<
      IMediaFile[],
      {
        signal?: AbortSignal;
        search?: string | undefined;
        typeGroup?: string | undefined;
        folderId?: string | undefined | null;
        archived?: boolean | undefined;
        includeSubfolders?: boolean;
        page: number;
        perPage: number;
      }
    >({
      query: ({ signal, ...params }) => ({
        url: "/media-library",
        method: "GET",
        signal,
        params,
      }),
    }),
    updateMediaLibFile: builder.mutation<
      IMediaFile,
      {
        signal?: AbortSignal;
        id: string;
        data: Partial<Omit<IMediaFile, "_id">>;
      }
    >({
      query: ({ signal, id, data }) => ({
        url: `/media-library/${id}`,
        method: "PUT",
        signal,
        body: data,
      }),
    }),
    bulkUpdateMediaLibFiles: builder.mutation<
      IMediaFile,
      {
        signal?: AbortSignal;
        ids: string[];
        data: Partial<Omit<IMediaFile, "_id">>;
      }
    >({
      query: ({ signal, ids, data }) => ({
        url: "/media-library/bulk",
        method: "PUT",
        signal,
        body: { ids, data },
      }),
    }),
    bulkDeleteMediaLibFiles: builder.mutation<
      { success: true } | null,
      {
        signal?: AbortSignal;
        ids: string[];
      }
    >({
      query: ({ signal, ids }) => ({
        url: "/media-library/bulk",
        method: "DELETE",
        signal,
        body: { ids },
      }),
    }),
    deleteMediaLibFile: builder.mutation<
      { success: true } | null,
      {
        signal?: AbortSignal;
        id: string;
      }
    >({
      query: ({ signal, id }) => ({
        url: `/media-library/${id}`,
        method: "DELETE",
        signal,
      }),
    }),
    extractWebsiteMedia: builder.mutation<
      IWebsiteMediaImportRsponse,
      {
        url: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ signal, url }) => ({
        url: "/media-library/website",
        method: "GET",
        signal,
        params: { url },
      }),
    }),
    optimize: builder.mutation({
      query: ({ url, signal }) => ({
        url: "/media-library/optimize",
        method: "POST",
        signal,
        body: { url },
      }),
    }),
    startMediaExport: builder.mutation<
      { jobId: string },
      { signal?: AbortSignal }
    >({
      query: ({ signal }) => ({
        url: "/media-library/export",
        method: "POST",
        signal,
      }),
    }),
    pollMediaExport: builder.mutation<
      { status: "processing" | "done" | "error"; url?: string; error?: string },
      { jobId: string; signal?: AbortSignal }
    >({
      query: ({ jobId, signal }) => ({
        url: `/media-library/export/${jobId}`,
        method: "GET",
        signal,
      }),
    }),
  }),
});

export const {
  useGetMediaLibFilesMutation,
  useUpdateMediaLibFileMutation,
  useDeleteMediaLibFileMutation,
  useExtractWebsiteMediaMutation,
  useBulkUpdateMediaLibFilesMutation,
  useBulkDeleteMediaLibFilesMutation,
  useOptimizeMutation,
  useStartMediaExportMutation,
  usePollMediaExportMutation,
} = mediaLibApi;
